import React, { useState, useRef } from "react";
import * as firebase from "firebase";
import TimeAgo from "javascript-time-ago";
import en from "javascript-time-ago/locale/en";
import { Button } from "reactstrap";

import { Markdown } from "components/Markdown";
import { useAuth } from "hooks/useAuth";
import { useProfile } from "hooks/useProfile";
import { useComments } from "hooks/useComments";
import "./comments.scss";

TimeAgo.addLocale(en);
const timeAgo = new TimeAgo("en-US");

const Comments = React.forwardRef((props, ref) => {
  const comments = useComments(props.exerciseId);
  // XXX: props.show is a hack to allow comments to be preloaded
  //      The Right Way would be to extract dumb components move logic up the chain
  return (
    <div
      ref={ref}
      className={`d-flex flex-column comments-container ${
        !props.show ? "hidden" : ""
      }`}
    >
      <div className="comments-header">
        <div className="comments-header-title">Comments</div>
      </div>

      <div className="flex-fill comments-list-container">
        <CommentInput exerciseId={props.exerciseId} />

        {!comments ? (
          "Loading comments..."
        ) : (
          <>
            {comments.length === 0 ? (
              <div className="comments-empty-list-msg">
                Looks like you're the first SF member here. Start the discussion
              </div>
            ) : (
              [...comments]
                .sort((a, b) => a.date - b.date)
                // get "root" comments (not replies)
                .filter(comment => !comment.replyToCommentId)
                .map(comment => ({
                  ...comment,
                  replies: comments.filter(
                    potentialReply =>
                      potentialReply.replyToCommentId === comment.id
                  )
                }))
                .map((comment, idx) => (
                  <Comment
                    exerciseId={props.exerciseId}
                    key={`comment-${idx}`}
                    comment={comment}
                    isReply={false}
                  />
                ))
            )}
          </>
        )}
      </div>
    </div>
  );
});

const CommentInput = React.forwardRef((props, ref) => {
  const { commentId, exerciseId } = props;
  const auth = useAuth();
  const isReply = Boolean(commentId);
  const thisRef = useRef();
  if (!ref) {
    ref = thisRef;
  }

  return (
    <div className={`comment-container ${isReply ? "reply" : ""}`}>
      <CommentUserAvatar url={auth && auth.avatarUrl} />
      <textarea
        className="comment-input"
        onKeyPress={onKeyPress}
        ref={ref}
        placeholder={
          isReply ? "Add a reply..." : "Ask a question or share an insight..."
        }
      />
    </div>
  );
  function onKeyPress() {
    var key = window.event.keyCode;
    if (key === 13 && ref && ref.current) {
      const data = {
        content: ref.current.value,
        date: new Date().getTime(),
        exerciseId,
        upvotes: 0,
        userId: auth.user.uid,
        ...(commentId ? { replyToCommentId: commentId } : {})
      };
      firebase.firestore().collection("comments").add(data);
      ref.current.blur();
      ref.current.value = "";
      return false;
    } else {
      return true;
    }
  }
});

function Comment(props) {
  const { comment, isReply, exerciseId } = props;
  const author =  useProfile(comment.userId);
  const [thumbs, setThumbs] = useState();
  const commentInputRef = useRef();

  if (!author) return <></>;

  return (
    <>
      <div className={`comment-container ${isReply ? "reply" : ""}`}>
        <CommentUserAvatar url={author.avatarUrl} />
        <div className="comment-content-wrapper">
          <div className="comment-username">
            {author.username} • {timeAgo.format(comment.date)}
          </div>
          <div className="comment-content">
            <Markdown transparent>{comment.content}</Markdown>
          </div>
          <div className="comment-actions">
            <Button
              className={`btn-comment-action ${thumbs === "up" ? "up" : ""}`}
              onClick={onClickThumb("up")}
            >
              <img alt="Thumbs Up" src="/img/icons/thumbs-up-font-light.svg" />
            </Button>
            {comment.upvotes}
            <Button
              className={`btn-comment-action ${
                thumbs === "down" ? "down" : ""
              }`}
              onClick={onClickThumb("down")}
            >
              <img
                alt="Thumbs Down"
                src="/img/icons/thumbs-down-font-light.svg"
              />
            </Button>

            {!isReply && (
              <Button
                className={`btn-comment-action`}
                onClick={() => {
                  commentInputRef.current && commentInputRef.current.focus();
                }}
              >
                <img alt="Reply" src="/img/icons/reply.svg" />
              </Button>
            )}

            <a
              href={`mailto:ed@solvefaction.com?subject=Problem with comment ${comment.id}`}
              onClick={() => {
                window.analytics.track("comment.reportProblem", {
                  id: comment.id
                });
              }}
            >
              <Button className="btn-comment-action">Report</Button>
            </a>
          </div>
        </div>
      </div>
      {!isReply && (
        <>
          {comment.replies.map((reply, i) => (
            <Comment
              key={`reply-${i}`}
              comment={reply}
              isReply={true}
              exerciseId={exerciseId}
            />
          ))}
          <CommentInput
            exerciseId={exerciseId}
            commentId={comment.id}
            ref={commentInputRef}
          />
        </>
      )}
    </>
  );

  function onClickThumb(direction) {
    if (!["up", "down"].includes(direction)) {
      throw Error(direction);
    }
    return () => {
      if (direction !== thumbs) {
        window.analytics.track("comment.thumbs", {
          commentId: comment.id,
          direction
        });
        comment.ref.set(
          { upvotes: comment.upvotes + (direction === "up" ? 1 : -1) },
          { merge: true }
        );
        setThumbs(direction);
      }
    };
  }
}

function CommentUserAvatar(props) {
  const { url } = props;
  return (
    <div className="comment-avatar">
      <img src={url} alt="Profile Pic" />
    </div>
  );
}

export { Comments };
